import { Chip, HSpacer, IconButton, Text, TextLink } from "@/components/DesignSystem";
import { FilterChip } from "@/components/DesignSystem/Toolbar/FilterChip";
import { FilterMenu } from "@/components/DesignSystem/Toolbar/FilterMenu";
import { FilterSheet } from "@/components/DesignSystem/Toolbar/FilterSheet";
import {
  Filter,
  FilterSelection,
  FilterSelections,
  NonBooleanFilter,
} from "@/components/DesignSystem/Toolbar/interfaces";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Tune from "@mui/icons-material/Tune";
import { Box, Stack, useTheme } from "@mui/material";
import _ from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";

interface FilterBarProps {
  filters: Filter[],
  hideAllFilter?: boolean
  onChange?: (selections: FilterSelections) => void,
  selections: FilterSelections,
  showClearAllButton?: boolean,
  testID: string,
  themeColor?: string,
}

export const FilterBar = ({
  filters = [],
  hideAllFilter = false,
  onChange,
  selections,
  showClearAllButton = false,
  testID,
  themeColor,
}: FilterBarProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const theme = useTheme();
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLDivElement | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
    scrollContainerRef.current?.addEventListener('scroll', updateScrollButtons);
  };
  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
    scrollContainerRef.current?.addEventListener('scroll', updateScrollButtons);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [filters, selections]);

  const selectedFilter = useMemo(() => (
    selectedFilterId
      ? filters.find((filter) => (
        filter.id === selectedFilterId && filter.selectionMethod !== 'boolean'
      ))
      : undefined
  ), [filters, selectedFilterId]);

  const handleSelectionChange = useCallback((filterId: string, selection: FilterSelection) => {
    const newSelections = _.cloneDeep(selections);
    newSelections.set(filterId, selection);
    onChange?.(newSelections);
  }, [onChange, selections]);

  const hasSelectedFilters = () => {
    if (selections.size > 0) {
      for (const selection of selections.values()) {
        if (selection.size > 0) {
          return true;
        }
      }
    }
    return false;
  };

  const ScrollButtons = (
    <>
      {canScrollLeft && (
        <IconButton
          className="scroll-button"
          onClick={scrollLeft}
          sx={{
            bgcolor: theme.palette.background.default,
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 1px 3px 1px rgba(0, 0, 0, 0.15);',
            opacity: 0,
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'opacity 0.3s ease-in-out',
            zIndex: 1,
            '&:hover': {
              bgcolor: theme.palette.background.default,
              opacity: 1,
            },
          }}
          testID="filter-bar-scroll-left-button"
        >
          <ChevronLeft />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          className="scroll-button"
          onClick={scrollRight}
          sx={{
            bgcolor: theme.palette.background.default,
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 1px 3px 1px rgba(0, 0, 0, 0.15);',
            opacity: 0,
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'opacity 0.3s ease-in-out',
            zIndex: 1,
            '&:hover': {
              bgcolor: theme.palette.background.default,
              opacity: 1,
            },
          }}
          testID="filter-bar-scroll-right-button"
        >
          <ChevronRight />
        </IconButton>
      )}
    </>
  );

  return (
    <>
      <Box
        position="relative"
        sx={{
          '&:hover .scroll-button': {
            opacity: 1,
            transition: 'opacity 0.3s ease-in-out',
          },
        }}
        width="100%"
      >
        {ScrollButtons}
        <Stack
          alignItems="center"
          className="scrollable-container"
          direction="row"
          flexWrap="nowrap"
          ref={scrollContainerRef}
          rowGap="8px"
        >
          {!hideAllFilter &&
            <Stack direction="row">
              <Chip
                icon={<Tune color="primary" />}
                label={
                  !isMobile ? (
                    <Text category="label-large">
                      {t(($) => $.designSystem.filter.allFilters)}
                    </Text>
                  ) : undefined
                }
                onClick={() => {
                  setSelectedFilterId(null);
                  setShowFilterSheet(true);
                }}
                size="medium"
                testID={`${testID}-chip-all-filters`}
                variant="outlined"
              />
              <HSpacer size="3" />
            </Stack>
          }
          {filters.map((filter) => (
            <Fragment key={filter.id}>
              <FilterChip
                disabled={!filter.options.length && !filter.onSearch}
                filter={filter}
                onClick={(event) => {
                  if (filter.selectionMethod === 'boolean') {
                    handleSelectionChange(
                      filter.id,
                      selections.get(filter.id)?.has(filter.id)
                        ? new Set()
                        : new Set([filter.options[0].id]),
                    );
                  } else if (!isMobile) {
                    setSelectedFilterId(filter.id);
                    setMenuAnchorEl(event.currentTarget);
                  } else {
                    setSelectedFilterId(filter.id);
                    setShowFilterSheet(true);
                  }
                }}
                selection={selections.get(filter.id)}
                testID={`${testID}-chip-${filter.id}`}
              />
              <HSpacer size="3" />
            </Fragment>
          ))}
          {showClearAllButton && hasSelectedFilters() && (
            <>
              <HSpacer size="4" />
              <TextLink
                category="title-medium"
                onClick={() => onChange?.(new Map())}
                sx={{ whiteSpace: 'nowrap' }}
                testID={`${testID}-clear-all-button`}
              >
                {t(($) => $.common.actions.clearAll)}
              </TextLink>
            </>
          )}
        </Stack>
      </Box>
      {(!isMobile && selectedFilter) && (
        <FilterMenu
          anchorEl={menuAnchorEl}
          filter={selectedFilter as NonBooleanFilter}
          onChange={(selection) => {
            if (selectedFilter.selectionMethod === 'single-select') {
              setSelectedFilterId(null);
            }
            handleSelectionChange(selectedFilter.id, selection);
          }}
          onClose={() => setSelectedFilterId(null)}
          open
          selection={selections.get(selectedFilter.id)}
          testID={`${testID}-menu`}
          themeColor={themeColor}
        />
      )}
      <FilterSheet
        filters={selectedFilter ? [selectedFilter] : filters}
        onApply={onChange}
        onClose={() => setShowFilterSheet(false)}
        open={showFilterSheet}
        selections={selections}
        testID={`${testID}-sheet`}
        themeColor={themeColor}
        title={selectedFilter ? selectedFilter.label : t(($) => $.designSystem.filter.filters)}
      />
    </>
  );
};

